import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { ApiError, claimInvitation } from "@/lib/api.ts";
import { getLineIdToken, loginWithLine } from "@/lib/liff.ts";

type State =
	| { kind: "loading" }
	| { kind: "line_login_required" }
	| { kind: "invitation_missing" }
	| { kind: "success" }
	| { kind: "error"; message: string };

export function Register() {
	const [, setLocation] = useLocation();
	const search = useSearch();
	const token = new URLSearchParams(search).get("token");
	const [state, setState] = useState<State>({ kind: "loading" });

	useEffect(() => {
		if (!token) {
			setState({ kind: "invitation_missing" });
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				const idToken = await getLineIdToken();
				if (!idToken) {
					if (!cancelled) setState({ kind: "line_login_required" });
					return;
				}
				await claimInvitation(token, idToken);
				if (!cancelled) {
					setState({ kind: "success" });
					setLocation("/shifts");
				}
			} catch (err) {
				if (cancelled) return;
				const message =
					err instanceof ApiError
						? mapClaimError(err)
						: err instanceof Error
							? err.message
							: "予期しないエラーが発生しました";
				setState({ kind: "error", message });
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [token, setLocation]);

	if (state.kind === "loading" || state.kind === "success") {
		return (
			<main className="flex min-h-screen items-center justify-center">
				<p className="text-sm text-gray-600">登録中...</p>
			</main>
		);
	}

	if (state.kind === "line_login_required") {
		return (
			<Card
				title="LINE ログインが必要です"
				body="ログインするとこの画面に戻り、登録が続行されます。"
				action={{
					label: "LINE でログイン",
					onClick: () => loginWithLine(window.location.href),
				}}
			/>
		);
	}

	if (state.kind === "invitation_missing") {
		return (
			<Card
				title="招待リンクが不正です"
				body="URL に token が含まれていません。お店から届いた招待リンクを開き直してください。"
			/>
		);
	}

	return <Card title="登録に失敗しました" body={state.message} />;
}

function mapClaimError(err: ApiError): string {
	if (err.status === 400) return "招待リンクが無効か、有効期限が切れています。";
	if (err.status === 409) return "この LINE アカウントは既に別のキャストに紐付いています。";
	return err.message;
}

function Card({
	title,
	body,
	action,
}: {
	title: string;
	body: string;
	action?: { label: string; onClick: () => void };
}) {
	return (
		<main className="mx-auto max-w-md px-4 py-16">
			<div className="rounded-2xl bg-white p-6 shadow-sm">
				<h1 className="text-lg font-semibold">{title}</h1>
				<p className="mt-3 text-sm leading-relaxed text-gray-700">{body}</p>
				{action ? (
					<button
						type="button"
						className="mt-6 w-full rounded-xl bg-green-600 py-3 text-sm font-semibold text-white active:bg-green-700"
						onClick={action.onClick}
					>
						{action.label}
					</button>
				) : null}
			</div>
		</main>
	);
}
